import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import { 
  Visibility,
  VisibilityOff,
  ContentCopy,
  AutoFixHigh,
  CheckCircle
} from "@mui/icons-material";
import { updateStaffPasswordOrFlag } from "../../utils/apiClient";
import { useTheme } from "../../ThemeContext";
import { message } from "antd";
import { useLogout } from "../../utils/hooks";

const ChangePassword = () => {
  const [visible, setVisible] = useState(true);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [generatedPassword, setGeneratedPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showGeneratedPassword, setShowGeneratedPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const navigate = useNavigate();
  const { currentTheme, themeMode } = useTheme();
  const logout = useLogout();

  const staffInfo = JSON.parse(sessionStorage.getItem("staff_info") || "null");
  const staffId = staffInfo?._id;
  const onboardingComplete = sessionStorage.getItem("onboardingComplete");
  
  // Check if onboarding is complete to determine redirect path
  const isOnboardingComplete = onboardingComplete === 'false' ? false : true;

  console.log(staffInfo, onboardingComplete, 'staffInfoDebugger')
  
  console.log("🔍 ChangePassword - Staff info:", { 
    hasOnboardingComplete: staffInfo?.hasOwnProperty('onboardingComplete'),
    onboardingComplete: onboardingComplete,
    isOnboardingComplete,
    willRedirectTo: isOnboardingComplete ? '/home' : '/onboarding/document-signing'
  });

  // Generate secure password with industry best practices
  const generateSecurePassword = () => {
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const specialChars = '!@#$%^&*()_+-=[]{}|;:,.<>?';
    
    // Ensure at least one character from each category
    const getRandomChar = (chars) => {
      const array = new Uint32Array(1);
      crypto.getRandomValues(array);
      return chars[array[0] % chars.length];
    };

    let newPassword = '';
    newPassword += getRandomChar(lowercase);
    newPassword += getRandomChar(uppercase);
    newPassword += getRandomChar(numbers);
    newPassword += getRandomChar(specialChars);

    // Fill remaining length with random characters from all categories
    const allChars = lowercase + uppercase + numbers + specialChars;
    const remainingLength = Math.floor(Math.random() * 5) + 4; // 4-8 more chars (total 8-12)
    
    for (let i = 0; i < remainingLength; i++) {
      newPassword += getRandomChar(allChars);
    }

    // Shuffle the password to avoid predictable patterns
    const passwordArray = newPassword.split('');
    for (let i = passwordArray.length - 1; i > 0; i--) {
      const array = new Uint32Array(1);
      crypto.getRandomValues(array);
      const j = array[0] % (i + 1);
      [passwordArray[i], passwordArray[j]] = [passwordArray[j], passwordArray[i]];
    }

    return passwordArray.join('');
  };

  const handleGeneratePassword = () => {
    const newPassword = generateSecurePassword();
    setGeneratedPassword(newPassword);
    setShowGeneratedPassword(true);
    clearErrors();
  };

  const handleCopyPassword = async () => {
    if (generatedPassword) {
      try {
        await navigator.clipboard.writeText(generatedPassword);
        setCopySuccess(true);
        // Auto-fill the password fields
        setPassword(generatedPassword);
        setConfirmPassword(generatedPassword);
        clearErrors();
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy password:', err);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = generatedPassword;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopySuccess(true);
        setPassword(generatedPassword);
        setConfirmPassword(generatedPassword);
        clearErrors();
        setTimeout(() => setCopySuccess(false), 2000);
      }
    }
  };

  const clearErrors = () => {
    setErrors({});
  };

  const validatePassword = () => {
    const newErrors = {};
    
    if (!password.trim()) {
      newErrors.password = "Password is required";
    } else if (password.length < 8 || password.length > 20) {
      newErrors.password = "Password must be between 8 and 20 characters";
    } else {
      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])[A-Za-z\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+$/;
      if (!passwordRegex.test(password)) {
        newErrors.password = "Password must contain at least one lowercase letter, one uppercase letter, one number, and one special character";
      }
    }
    
    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validatePassword()) return;
    
    setLoading(true);
    try {
      await updateStaffPasswordOrFlag(staffId, {
        password,
        changePassword: false,
      });
      message.success("Password updated successfully! You will be logged out shortly.");
      setVisible(false);
      setTimeout(() => {
        console.log(`🔄 Password updated - Logging out...`);
        logout();
      }, 3000);
    } catch (error) {
      console.error("Error updating password:", error);
      message.error("Failed to update password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    setLoading(true);
    try {
      await updateStaffPasswordOrFlag(staffId, { changePassword: false });
      message.success("Password change skipped!");
      console.log(staffInfo, isOnboardingComplete, 'isOnboardingCompleteDebugger')
      setVisible(false);
      setTimeout(() => {
        const redirectPath = isOnboardingComplete ? "/home" : "/onboarding/document-signing";
        console.log(`🔄 Password change skipped - Redirecting to: ${redirectPath}`);
        navigate(redirectPath);
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error("Error skipping password change:", error);
      message.error("Failed to skip password change. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Prevent closing the dialog - can only be closed via Skip or Update Password
  const handleClose = () => {
    // Do nothing - dialog cannot be closed via X or backdrop click
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
    if (errors.password) {
      setErrors(prev => ({ ...prev, password: "" }));
    }
  };

  const handleConfirmPasswordChange = (event) => {
    setConfirmPassword(event.target.value);
    if (errors.confirmPassword) {
      setErrors(prev => ({ ...prev, confirmPassword: "" }));
    }
  };

  return (
    <>
             <Dialog
         open={visible}
         onClose={handleClose}
         maxWidth="sm"
         fullWidth
         disableEscapeKeyDown={true}
         disableBackdropClick={true}
         PaperProps={{
          sx: {
            backgroundColor: themeMode === "dark" ? "#04002b" : "#ffffff",
            color: currentTheme.homePage?.textColor || (themeMode === "dark" ? "#ffffff" : "#000000"),
            borderRadius: "16px",
            border: themeMode === "dark" ? "1px solid #302d4c" : "1px solid #e9e8ea",
            minHeight: "40vh",
            fontFamily: '"Satoshi", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
            boxShadow: themeMode === "dark" 
              ? "0 20px 40px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255,255,255,0.1)"
              : "0 20px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0,0,0,0.05)",
          },
        }}
      >
                 <DialogTitle sx={{ pb: 2 }}>
           <Box display="flex" justifyContent="center" alignItems="center">
             <Typography
               variant="h4"
               sx={{
                 color: currentTheme.text?.primary || (themeMode === "dark" ? "#ffffff" : "#000000"),
                 marginTop: "2rem",
                 fontWeight: 600,
                 fontFamily: '"Satoshi", sans-serif',
                 fontSize: "1.75rem",
                 textAlign: "center",
                 "@media (max-width: 480px)": {
                   fontSize: "1.4rem",
                 },
               }}
             >
               Change Password
             </Typography>
           </Box>
         </DialogTitle>

        <DialogContent sx={{ pb: 3 }}>
          <Box component="form" sx={{ mt: 2 }}>
            <Typography
              variant="body1"
              sx={{
                color: "#FFA500",
                mb: 3,
                mt: -2,
                fontFamily: '"Satoshi", sans-serif',
                fontSize: "0.9rem",
                lineHeight: 1.5,
                fontWeight: 500,
                letterSpacing: "0.05rem",
              }}
            >
              We recommend you change the auto-generated password for better security.
            </Typography>

            {/* Password Generator Section */}
            <Box sx={{ mb: 3, p: 2, border: `1px solid ${themeMode === "dark" ? "#302d4c" : "#e9e8ea"}`, borderRadius: 2, backgroundColor: themeMode === "dark" ? "rgba(255,255,255,0.02)" : "#f9f9f9" }}>
              <Typography
                variant="body1"
                sx={{
                  color: currentTheme.text?.primary || (themeMode === "dark" ? "#ffffff" : "#000000"),
                  mb: 2,
                  fontFamily: '"Satoshi", sans-serif',
                  fontSize: "0.9rem",
                  fontWeight: 500,
                }}
              >
                Password Generator
              </Typography>
              
              <Box display="flex" gap={1} alignItems="center" mb={2}>
                <Button
                  variant="outlined"
                  startIcon={<AutoFixHigh />}
                  onClick={handleGeneratePassword}
                  disabled={loading}
                  sx={{
                    borderColor: "#FE4E3A",
                    color: "#FE4E3A",
                    textTransform: "none",
                    fontFamily: '"Satoshi", sans-serif',
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    "&:hover": {
                      borderColor: "#FF4444",
                      backgroundColor: themeMode === "dark" ? "rgba(254,78,58,0.1)" : "rgba(254,78,58,0.05)",
                    },
                  }}
                >
                  Generate Secure Password
                </Button>
              </Box>

              {generatedPassword && (
                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    value={generatedPassword}
                    type={showGeneratedPassword ? "text" : "password"}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowGeneratedPassword(!showGeneratedPassword)}
                            edge="end"
                          >
                            {showGeneratedPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                          <Tooltip title="Copy and use this password">
                            <IconButton
                              onClick={handleCopyPassword}
                              edge="end"
                              sx={{ color: "#FE4E3A" }}
                            >
                              <ContentCopy />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: themeMode === "dark" ? "rgba(255,255,255,0.05)" : "#ffffff",
                        borderRadius: 2,
                        fontFamily: '"Satoshi", monospace',
                        fontSize: "0.9rem",
                        "& fieldset": {
                          borderColor: themeMode === "dark" ? "#302d4c" : "#e9e8ea",
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: themeMode === "dark" ? "#ffffff" : "#000000",
                        fontFamily: '"Satoshi", monospace',
                        fontSize: "0.9rem",
                      },
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#FE4E3A",
                      fontFamily: '"Satoshi", sans-serif',
                      fontSize: "0.75rem",
                      mt: 0.5,
                      display: "block",
                    }}
                  >
                    Click the copy button to auto-fill the password fields below
                  </Typography>
                </Box>
              )}
            </Box>

            {/* New Password Field */}
            <Typography
              variant="body1"
              sx={{
                color: currentTheme.text?.primary || (themeMode === "dark" ? "#ffffff" : "#000000"),
                mb: 1,
                fontFamily: '"Satoshi", sans-serif',
                fontSize: "1rem",
                fontWeight: 500,
              }}
            >
              New Password*
            </Typography>
            <TextField
              fullWidth
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={handlePasswordChange}
              error={!!errors.password}
              helperText={errors.password}
              placeholder="Enter your new password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: themeMode === "dark" ? "rgba(255,255,255,0.05)" : "#f7f7f7",
                  borderRadius: 2,
                  fontFamily: '"Satoshi", sans-serif',
                  fontSize: "1rem",
                  "& fieldset": {
                    borderColor: themeMode === "dark" ? "#302d4c" : "#e9e8ea",
                  },
                  "&:hover fieldset": {
                    borderColor: "#FE4E3A",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#FE4E3A",
                  },
                },
                "& .MuiInputBase-input": {
                  color: themeMode === "dark" ? "#ffffff" : "#000000",
                  fontFamily: '"Satoshi", sans-serif',
                  fontSize: "1rem",
                  padding: "1rem",
                  "&::placeholder": {
                    color: themeMode === "dark" ? "#999999" : "#666666",
                    opacity: 1,
                  },
                },
                "& .MuiFormHelperText-root": {
                  color: "#FE4E3A",
                  fontFamily: '"Satoshi", sans-serif',
                  fontSize: "0.875rem",
                  marginTop: "0.25rem",
                },
              }}
            />

            {/* Confirm Password Field */}
            <Typography
              variant="body1"
              sx={{
                color: currentTheme.text?.primary || (themeMode === "dark" ? "#ffffff" : "#000000"),
                mb: 1,
                fontFamily: '"Satoshi", sans-serif',
                fontSize: "1rem",
                fontWeight: 500,
              }}
            >
              Confirm New Password*
            </Typography>
            <TextField
              fullWidth
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              placeholder="Re-enter your new password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: themeMode === "dark" ? "rgba(255,255,255,0.05)" : "#f7f7f7",
                  borderRadius: 2,
                  fontFamily: '"Satoshi", sans-serif',
                  fontSize: "1rem",
                  "& fieldset": {
                    borderColor: themeMode === "dark" ? "#302d4c" : "#e9e8ea",
                  },
                  "&:hover fieldset": {
                    borderColor: "#FE4E3A",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#FE4E3A",
                  },
                },
                "& .MuiInputBase-input": {
                  color: themeMode === "dark" ? "#ffffff" : "#000000",
                  fontFamily: '"Satoshi", sans-serif',
                  fontSize: "1rem",
                  padding: "1rem",
                  "&::placeholder": {
                    color: themeMode === "dark" ? "#999999" : "#666666",
                    opacity: 1,
                  },
                },
                "& .MuiFormHelperText-root": {
                  color: "#FE4E3A",
                  fontFamily: '"Satoshi", sans-serif',
                  fontSize: "0.875rem",
                  marginTop: "0.25rem",
                },
              }}
            />

            {/* Action Buttons */}
            <Box display="flex" justifyContent="flex-end" gap={2} sx={{ mt: 4 }}>
              <Button
                variant="outlined"
                onClick={handleSkip}
                disabled={loading}
                sx={{
                  borderColor: themeMode === "dark" ? "#302d4c" : "#666",
                  color: themeMode === "dark" ? "#ffffff" : "#666",
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 600,
                  fontFamily: '"Satoshi", sans-serif',
                  "&:hover": {
                    borderColor: "#888",
                    backgroundColor: themeMode === "dark" ? "rgba(255,255,255,0.05)" : "#f5f5f5",
                  },
                  "&:disabled": {
                    borderColor: themeMode === "dark" ? "#302d4c" : "#ccc",
                    color: themeMode === "dark" ? "#666" : "#999",
                  },
                  "@media (max-width: 480px)": {
                    px: 3,
                    py: 1.25,
                    fontSize: "0.875rem",
                  },
                }}
              >
                Skip
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                sx={{
                  background: "#FE4E3A",
                  color: "#fff",
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 600,
                  fontFamily: '"Satoshi", sans-serif',
                  "&:hover": {
                    background: "#FF4444",
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(255, 92, 92, 0.3)",
                  },
                  "&:disabled": {
                    background: themeMode === "dark" ? "#302d4c" : "#999",
                    color: "#cccccc",
                    transform: "none",
                    boxShadow: "none",
                  },
                  "@media (max-width: 480px)": {
                    px: 3,
                    py: 1.25,
                    fontSize: "0.875rem",
                  },
                }}
              >
                {loading ? "Updating..." : "Update Password"}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Copy Success Snackbar */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={2000}
        onClose={() => setCopySuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setCopySuccess(false)}
          severity="success"
          sx={{
            width: '100%',
            backgroundColor: themeMode === "dark" ? "#1a5d1a" : "#d4edda",
            color: themeMode === "dark" ? "#ffffff" : "#155724",
            "& .MuiAlert-icon": {
              color: themeMode === "dark" ? "#4caf50" : "#28a745",
            },
          }}
          icon={<CheckCircle />}
        >
          Password copied and auto-filled!
        </Alert>
      </Snackbar>
    </>
  );
};

export default ChangePassword;